import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  X,
  Check,
  Sparkles,
  Copy,
  RotateCcw,
  Maximize2,
  Minimize2,
  Type,
} from "lucide-react";
import { AvailableVariable } from "./types";
import { NodeDataSidebar } from "../shared/NodeDataSidebar";

interface PromptEditorWithVariablesModalProps {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (value: string) => void;
  title?: string;
  description?: string;
  placeholder?: string;
  availableVariables: AvailableVariable[];
}

export const PromptEditorWithVariablesModal: React.FC<
  PromptEditorWithVariablesModalProps
> = ({
  isOpen,
  onClose,
  value,
  onChange,
  title = "Edit Prompt",
  description = "Write your instructions for the AI model",
  placeholder = "Enter your prompt here...",
  availableVariables,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPositionRef = useRef<number>(0);
  const prevIsOpenRef = useRef<boolean>(false);

  // Track cursor position on every selection change
  const updateCursorPosition = useCallback(() => {
    if (textareaRef.current) {
      cursorPositionRef.current = textareaRef.current.selectionStart;
    }
  }, []);

  // Only sync value when modal OPENS (transitions from closed to open)
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (justOpened) {
      // Modal just opened - sync the value from props
      setLocalValue(value);
      setTimeout(() => {
        textareaRef.current?.focus();
        // Set cursor at end of text
        if (textareaRef.current) {
          const len = value.length;
          textareaRef.current.setSelectionRange(len, len);
          cursorPositionRef.current = len;
        }
      }, 100);
    }
  }, [isOpen, value]);

  const handleSave = () => {
    onChange(localValue);
    onClose();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(localValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReset = () => {
    setLocalValue(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
    if (e.key === "Tab") {
      e.preventDefault();
      const textarea = textareaRef.current;
      if (!textarea) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue =
        localValue.substring(0, start) + "  " + localValue.substring(end);
      setLocalValue(newValue);
      setTimeout(() => {
        textarea.setSelectionRange(start + 2, start + 2);
      }, 0);
    }
  };

  // Handle drag start from NodeDataSidebar - wrap in {{ }}
  const handleSidebarDragStart = (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output"
  ) => {
    e.stopPropagation();
    // Format: {{NodeName.outputs.variableName}} or {{NodeName.outputs}} for entire object
    const path = type === "input" ? "inputs" : "outputs";
    const fullVariable = variable
      ? `{{${nodeName}.${path}.${variable}}}`
      : `{{${nodeName}.${path}}}`;
    e.dataTransfer.setData("text/plain", fullVariable);
    e.dataTransfer.effectAllowed = "copy";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const variable = e.dataTransfer.getData("text/plain");
    if (variable) {
      const textarea = textareaRef.current;
      if (textarea) {
        // Use the stored cursor position (where user last clicked/typed)
        const insertPosition = cursorPositionRef.current;

        const newValue =
          localValue.substring(0, insertPosition) + variable + localValue.substring(insertPosition);
        setLocalValue(newValue);

        setTimeout(() => {
          const newCursorPos = insertPosition + variable.length;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
          textarea.focus();
          cursorPositionRef.current = newCursorPos;
        }, 0);
      } else {
        setLocalValue((prev) => prev + variable);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set false if we're actually leaving the drop zone
    const rect = e.currentTarget.getBoundingClientRect();
    if (
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom
    ) {
      setIsDraggingOver(false);
    }
  };

  const wordCount = localValue.trim()
    ? localValue.trim().split(/\s+/).length
    : 0;
  const charCount = localValue.length;

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className={`bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border border-slate-200 ring-1 ring-black/5 transition-all duration-300 ${
          isFullscreen
            ? "w-[98vw] h-[98vh]"
            : "w-[95vw] max-w-[1200px] h-[80vh] max-h-[700px]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-indigo-50 to-purple-50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-sm text-white">
              <Sparkles size={18} />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-800 tracking-tight">
                {title}
              </h2>
              <p className="text-xs text-slate-500">{description}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition-colors"
              title="Copy to clipboard"
            >
              {copied ? (
                <Check size={16} className="text-green-500" />
              ) : (
                <Copy size={16} />
              )}
            </button>
            <button
              onClick={handleReset}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition-colors"
              title="Reset changes"
            >
              <RotateCcw size={16} />
            </button>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-lg transition-colors"
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>

            <div className="h-6 w-px bg-slate-200 mx-1" />

            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Using NodeDataSidebar */}
          <NodeDataSidebar
            availableVariables={availableVariables}
            onDragStart={handleSidebarDragStart}
          />

          {/* Editor Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div
              className={`flex-1 overflow-hidden bg-white relative ${
                isDraggingOver ? "ring-2 ring-inset ring-indigo-400 bg-indigo-50/30" : ""
              }`}
            >
              <textarea
                ref={textareaRef}
                value={localValue}
                onChange={(e) => {
                  setLocalValue(e.target.value);
                  updateCursorPosition();
                }}
                onKeyDown={handleKeyDown}
                onKeyUp={updateCursorPosition}
                onClick={updateCursorPosition}
                onSelect={updateCursorPosition}
                onFocus={updateCursorPosition}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                placeholder={placeholder}
                className="w-full h-full p-5 font-mono text-sm bg-transparent text-slate-700 resize-none focus:outline-none selection:bg-indigo-200 leading-relaxed placeholder:text-slate-400"
                spellCheck={false}
              />
              {isDraggingOver && (
                <div className="absolute top-2 right-2 pointer-events-none">
                  <div className="px-3 py-1.5 bg-indigo-500 text-white rounded-lg text-xs font-medium shadow-lg">
                    Drop to insert variable
                  </div>
                </div>
              )}
            </div>

            {/* Status Bar */}
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 flex-shrink-0">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1.5">
                  <Type size={12} />
                  {charCount} characters
                </span>
                <span>{wordCount} words</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-[10px] font-mono">
                  Ctrl
                </kbd>
                <span>+</span>
                <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-[10px] font-mono">
                  Enter
                </kbd>
                <span className="text-slate-400">to save</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-100 bg-white flex justify-between items-center flex-shrink-0">
          <div className="text-xs text-slate-400">
            Press{" "}
            <kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px] font-mono">
              Esc
            </kbd>{" "}
            to close
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-50 hover:text-slate-800 rounded-lg font-medium text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg shadow-md hover:shadow-indigo-500/25 font-semibold text-sm transition-all transform active:scale-95 flex items-center gap-2"
            >
              <Check size={16} />
              Save Prompt
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
